import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import Logo from "../../../assets/Logo-Negro.png";
import "../styles/SancionesForm.css";
import { getMotivosOptions, getMotivoConfig, generarMensajeMotivo } from "../../../config/motivos.config";
import FirmasPad from "../../../components/FirmasPad";
import { createInforme } from "../../../api/informes.api";
import { getEmpleadoByLegajo } from "../../../api/empleados.api";

const InformeForm = () => {
  const user = JSON.parse(localStorage.getItem('user'))
  
  const obtenerNombreInspector = () => {
    const inspector = JSON.parse(localStorage.getItem("user"));
    return inspector?.Nombre || "Inspector No Identificado";
  };

  // Obtener fecha y hora actual en formato YYYY-MM-DD y HH:MM
  const obtenerFechaActual = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const obtenerHoraActual = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  const [formData, setFormData] = useState({
    informeNumero: "",
    lugarControl: "",
    lugarAbandono: "",
    fecha: obtenerFechaActual(),
    hora: obtenerHoraActual(),
    actuante: obtenerNombreInspector(),
    motivo: "",
    motivoDetalle: "",
    empleado: "",
    legajo: "",
    nroCoche: "",
    linea: "",
    horaPresentacion: "",
    servicio: "",
    rutaRecorrido: "",
    rutaFueraRecorrido: "",
    velocidad: "",
    fallaMecanica: "",
    fechaCM: "",
    fechaDenuncia: "",
    fechaSiniestro: "",
    horaSiniestro: "",
    lugarSiniestro: "",
    marcaVehiculo: "",
    dominioVehiculo: "",
    msgRoturas: "",
    textoInforme: "",
    lugarPresentacion: "",
    // Nuevos campos corregidos
    horarioPasoTeorico: "",
    tomaServicio: "",
    detalleVestimenta: "",
    horaFin: "",
    horaLlegada: "",
    horaSalidaProgramada: "",
    horaSalidaReal: "",
    lugarAscenso: "",
    horaAscenso: "",
    zonaHojaRuta: "",
    horaHojaRuta: ""
  });

  // Estados para imagen y firma
  const [imagenBase64, setImagenBase64] = useState(null);
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [mostrarModalFirma, setMostrarModalFirma] = useState(false);
  const [imagenPreview, setImagenPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Generar mensaje automáticamente con useMemo
  const mensajeGenerado = useMemo(() => {
    if (!formData.motivo) return '';
    return generarMensajeMotivo(formData.motivo, formData);
  }, [formData]);

  // Obtener configuración del motivo actual
  const motivoConfig = useMemo(() => {
    if (!formData.motivo) return null;
    return getMotivoConfig(formData.motivo);
  }, [formData.motivo]);

  // Ya no necesitamos obtener el número de informe previamente
  // El NroInforme se asignará automáticamente con el ID en el backend
  useEffect(() => {
    // Limpiar timeout al desmontar componente
    return () => {
      if (legajoTimeoutRef.current) {
        clearTimeout(legajoTimeoutRef.current);
      }
    };
  }, []);


  // Ref para el timeout del debounce
  const legajoTimeoutRef = useRef(null);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Búsqueda de empleado con debounce (solo después de dejar de escribir)
    if (name === "legajo") {
      // Limpiar el empleado mientras escribe
      setFormData((prev) => ({ ...prev, empleado: "" }));
      
      // Cancelar búsqueda anterior si está escribiendo
      if (legajoTimeoutRef.current) {
        clearTimeout(legajoTimeoutRef.current);
      }

      // Solo buscar si hay valor y esperar 800ms después de dejar de escribir
      if (value && value.length >= 2) {
        legajoTimeoutRef.current = setTimeout(async () => {
          try {
            const data = await getEmpleadoByLegajo(value);
            setFormData((prev) => ({ ...prev, empleado: data.nombre }));
            
            // Advertir si el empleado está dado de baja
            if (data.Baja === 1) {
              alert(`⚠️ ADVERTENCIA: El empleado ${data.nombre} está dado de baja en el sistema.`);
            }
          } catch (error) {
            console.error("❌ Error al obtener nombre del empleado:", error);
            setFormData((prev) => ({ ...prev, empleado: "" }));
            
            // Solo mostrar error 404 si es un legajo largo (probablemente completo)
            if (error.response?.status === 404 && value.length >= 3) {
              const errorData = error.response?.data;
              let mensaje = `⚠️ No se encontró un empleado con el legajo "${value}".`;
              
              // Mostrar sugerencias si están disponibles
              if (errorData?.similares && errorData.similares.length > 0) {
                mensaje += '\n\n📋 ¿Busca alguno de estos?\n';
                mensaje += errorData.similares.slice(0, 5).map(e => 
                  `• Legajo ${e.Legajo}: ${e.NmEmpleado}`
                ).join('\n');
              }
              
              alert(mensaje);
            }
          }
        }, 800); // Esperar 800ms después de dejar de escribir
      }
    }
  };

  const handleMotivoChange = (e) => {
    const motivo = e.target.value;
    setFormData((prev) => ({ ...prev, motivo, motivoDetalle: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que tenga firma
    if (!firmaBase64) {
      alert("⚠️ Debes firmar el informe antes de guardarlo");
      return;
    }

    // Validar que motivo "Otros" tenga texto manual
    if (formData.motivo === 'Otros' && !formData.motivoDetalle?.trim()) {
      alert("⚠️ El motivo 'Otros' requiere que escribas un mensaje personalizado");
      return;
    }
    
    setMostrarModalFirma(false);
    guardarInforme();
  };

  const guardarInforme = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      // Determinar el mensaje final (manual o autogenerado)
      const mensajeFinal = motivoConfig?.allowManualMessage && formData.motivoDetalle 
        ? formData.motivoDetalle 
        : mensajeGenerado;

      // Guardar el informe y los archivos en un solo paso
      const payload = {
        FechaRealizado: formData.fecha,
        Hora: formData.hora,
        Motivo: formData.motivo,
        MotivoDetalle: mensajeFinal,
        LegajoEmp: formData.legajo,
        NombreEmp: formData.empleado,
        Servicio: formData.servicio,
        Linea: formData.linea,
        CocheInterno: formData.nroCoche,
        Estado: "pendiente",
        RealizadoPor: user?.Legajo,
        FirmaInspector: firmaBase64 || null,
        ImagenAdjunta: imagenBase64 || null
      };

      const response = await createInforme(payload);

      alert(`✅ Informe N° ${response.NroInforme} guardado exitosamente en la base de datos`);
      
      // Limpiar formulario después de un breve delay para que el usuario vea el mensaje
      setTimeout(() => {
        limpiarFormulario();
      }, 500);
    } catch (error) {
      console.error("❌ Error al guardar informe:", error);
      const errorMsg = error.response?.data?.error || "Error desconocido";
      alert(`❌ No se pudo guardar el informe. ${errorMsg}`);
    }
  };

  const limpiarFormulario = () => {
    // Resetear todos los campos del formulario
    setFormData({
      informeNumero: "",
      lugarControl: "",
      lugarAbandono: "",
      fecha: obtenerFechaActual(),
      hora: obtenerHoraActual(),
      actuante: obtenerNombreInspector(),
      motivo: "",
      motivoDetalle: "",
      empleado: "",
      legajo: "",
      nroCoche: "",
      linea: "",
      horaPresentacion: "",
      servicio: "",
      rutaRecorrido: "",
      rutaFueraRecorrido: "",
      velocidad: "",
      fallaMecanica: "",
      fechaCM: "",
      fechaDenuncia: "",
      fechaSiniestro: "",
      msgRoturas: "",
      textoInforme: "",
      lugarPresentacion: "",
      horarioPasoTeorico: "",
      tomaServicio: "",
      detalleVestimenta: "",
      horaFin: "",
      horaLlegada: "",
      horaSalidaProgramada: "",
      horaSalidaReal: "",
      lugarAscenso: "",
      horaAscenso: "",
      zonaHojaRuta: "",
      horaHojaRuta: ""
    });

    // Limpiar imagen y firma
    setImagenBase64(null);
    setFirmaBase64(null);
    setImagenPreview(null);
    
    // Resetear input de archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Cerrar modal
    setMostrarModalFirma(false);
  };

  // Handler para campos extra dinámicos
  const renderCamposExtra = () => {
    if (!motivoConfig) return null;
    
    return motivoConfig.fields.map((campo) => {
      // Etiquetas legibles para los campos
      const labels = {
        horaPresentacion: "Hora de Presentación",
        lugarAbandono: "Lugar de Abandono",
        horarioPasoTeorico: "Horario Paso Teórico",
        servicio: "Servicio",
        rutaRecorrido: "Ruta de Recorrido",
        rutaFueraRecorrido: "Ruta Fuera de Recorrido",
        velocidad: "Velocidad (km/h)",
        fallaMecanica: "Falla Mecánica / Daños",
        fechaCM: "Fecha Control Médico",
        fechaDenuncia: "Fecha de Denuncia",
        fechaSiniestro: "Fecha del Siniestro",
        horaSiniestro: "Hora del Siniestro",
        lugarSiniestro: "Lugar del Siniestro",
        marcaVehiculo: "Marca del Vehículo",
        dominioVehiculo: "Dominio del Vehículo",
        msgRoturas: "Roturas",
        detalleVestimenta: "Detalle de Falta de Vestimenta",
        lugarPresentacion: "Lugar de Presentación",
        tomaServicio: "¿Se le permite tomar servicio?",
        horaFin: "Horario Programado de Llegada",
        horaLlegada: "Horario Real de Llegada",
        horaSalidaProgramada: "Horario Programado de Salida",
        horaSalidaReal: "Horario Real de Salida",
        lugarAscenso: "Lugar de Ascenso",
        horaAscenso: "Hora de Ascenso",
        zonaHojaRuta: "Zona de Hoja de Ruta",
        horaHojaRuta: "Hora de Hoja de Ruta"
      };

      const inputType = campo.startsWith('fecha') ? 'date' : 
                       campo.startsWith('hora') ? 'time' : 'text';

      return (
        <div key={campo}>
          <label>{labels[campo] || campo}</label>
          {campo === 'detalleVestimenta' && motivoConfig.allowManualMessage ? (
            <textarea
              name={campo}
              value={formData[campo] || ""}
              onChange={handleChange}
              rows={3}
              placeholder="Describa la falta de vestimenta..."
            />
          ) : campo === 'tomaServicio' ? (
            <select
              name={campo}
              value={formData[campo] || ""}
              onChange={handleChange}
            >
              <option value="">¿Se le permite tomar servicio?</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          ) : (
            <input
              type={inputType}
              name={campo}
              value={formData[campo] || ""}
              onChange={handleChange}
            />
          )}
        </div>
      );
    });
  };

  // Handler para eliminar imagen
  const eliminarImagen = () => {
    setImagenBase64(null);
    setImagenPreview(null);
  };

  // Handler para subir archivo
  const handleSubirArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new window.FileReader();
    reader.onloadend = () => {
      setImagenBase64(reader.result);
      setImagenPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handler para abrir modal de firma
  const abrirModalFirma = () => {
    setMostrarModalFirma(true);
  };

  // Handlers para firma
  const handleCancelarFirma = () => {
    setMostrarModalFirma(false);
  };

  const handleGuardarFirma = (firma) => {
    setFirmaBase64(firma);
    setMostrarModalFirma(false);
  };

  return (
    <form className="informe-form" onSubmit={handleSubmit}>
      <img src={Logo} className="logoInforme" alt="Logo" />
      <h2>Informe de Servicio</h2>

      <div className="form-grid">
        <div>
          <label>Informe N°</label>
          <input
            type="text"
            name="informeNumero"
            value="Se asignará automáticamente"
            readOnly
            style={{ fontStyle: 'italic', color: '#666' }}
          />
        </div>
        <div>
          <label>Lugar de Control</label>
          <input
            type="text"
            name="lugarControl"
            value={formData.lugarControl}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Fecha</label>
          <input
            type="date"
            name="fecha"
            value={formData.fecha}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Hora</label>
          <input
            type="time"
            name="hora"
            value={formData.hora}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Actuante</label>
          <input
            type="text"
            name="actuante"
            value={user.Legajo}
            onChange={handleChange}
            placeholder="Se auto-completa con el inspector"
          />
        </div>
        <div>
          <label>Motivo</label>
          <select
            name="motivo"
            value={formData.motivo}
            onChange={handleMotivoChange}
          >
            <option value="">Seleccione un motivo</option>
            {getMotivosOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Legajo N°</label>
          <input
            type="text"
            name="legajo"
            value={formData.legajo}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Usuario</label>
          <input
            type="text"
            name="empleado"
            value={formData.empleado}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>N° de Coche</label>
          <input
            type="text"
            name="nroCoche"
            value={formData.nroCoche}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Línea</label>
          <input
            type="text"
            name="linea"
            value={formData.linea}
            onChange={handleChange}
          />
        </div>
        <div>
          <label>Servicio</label>
          <input
            type="text"
            name="servicio"
            value={formData.servicio}
            onChange={handleChange}
          />
        </div>


        {/* Campos dinámicos */}
        {renderCamposExtra()}
      </div>

      {/* Campo de detalle del motivo */}
      {formData.motivo && (
        <div className="full-width">
          <label>
            {motivoConfig?.allowManualMessage ? 'Mensaje (Editable)' : 'Mensaje generado'}
          </label>
          <textarea
            name="motivoDetalle"
            value={motivoConfig?.allowManualMessage ? formData.motivoDetalle : mensajeGenerado}
            onChange={motivoConfig?.allowManualMessage ? handleChange : undefined}
            rows={4}
            readOnly={!motivoConfig?.allowManualMessage}
            className={motivoConfig?.allowManualMessage ? "" : "campo-auto-generado"}
            placeholder={motivoConfig?.allowManualMessage ? "Escriba el mensaje del informe..." : ""}
          />
          <small className="texto-ayuda">
            {motivoConfig?.allowManualMessage 
              ? 'Complete el mensaje del informe manualmente' 
              : 'Este mensaje se genera automáticamente según los datos ingresados'}
          </small>
        </div>
      )}

      {/* Sección de Imagen Adjunta (Opcional) */}
      <div className="full-width imagen-section">
        <label>Imagen Adjunta (Opcional)</label>
        <div className="imagen-controls">
          <Button 
            variant="secondary" 
            onClick={() => fileInputRef.current?.click()}
            className="btn-imagen"
          >
            📁 Subir Imagen desde Dispositivo
          </Button>
          {imagenPreview && (
            <Button 
              variant="danger" 
              onClick={eliminarImagen}
              className="btn-imagen"
            >
              🗑️ Eliminar Imagen
            </Button>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleSubirArchivo}
        />
        {imagenPreview && (
          <div className="imagen-preview">
            <img src={imagenPreview} alt="Preview" style={{ maxWidth: "300px", marginTop: "10px" }} />
          </div>
        )}
      </div>

      {/* Botón para firmar y guardar */}
      <div className="botones-accion">
        {firmaBase64 ? (
          <>
            <div className="firma-confirmada">
              ✅ Informe firmado
              <img src={firmaBase64} alt="Firma" style={{ maxWidth: "200px", marginLeft: "15px", border: "1px solid #ccc" }} />
            </div>
            <button type="button" onClick={handleSubmit} className="pdf-button">
              💾 Guardar Informe
            </button>
            <button 
              type="button" 
              onClick={limpiarFormulario} 
              className="pdf-button" 
              style={{ backgroundColor: '#6c757d', marginLeft: '10px' }}
            >
              🔄 Limpiar Formulario
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={abrirModalFirma} className="pdf-button">
              ✍️ Firmar y Guardar Informe
            </button>
            <button 
              type="button" 
              onClick={limpiarFormulario} 
              className="pdf-button" 
              style={{ backgroundColor: '#6c757d', marginLeft: '10px' }}
            >
              🔄 Limpiar Formulario
            </button>
          </>
        )}
      </div>
      
      {/* Modal de Firma */}
      <Modal show={mostrarModalFirma} onHide={handleCancelarFirma} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Firmar Informe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Por favor, firme el informe para continuar:</p>
          <FirmasPad 
            onFirmar={handleGuardarFirma}
            onCancelar={handleCancelarFirma}
            fileName={`informe_${formData.fecha}_${formData.legajo}`}
          />
        </Modal.Body>
      </Modal>
    </form>
  );
};

export default InformeForm;
